import React, { useCallback, useEffect } from 'react'
import { Auth } from 'aws-amplify'
import { Badge, Button, Col, Form, FormText, Modal, Row } from 'react-bootstrap'
import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { UserMfaPreference } from '../../../../back-end/common/authenticate'
import QRCode from 'react-qr-code'
import { MessageAction } from '../ui/messages/message'
import { Copy } from 'react-feather'
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import { getTOTPURI } from '../../utilities/mfa'

interface TwoFactorModalProps {
	isOpen: boolean
	onClose: () => void
	updateMessages: (action: MessageAction) => void
}

export const TwoFactorModal = (props: TwoFactorModalProps) => {
	const { appState } = React.useContext(AppContext)

	const [userPreferences, setUserPreferences] = React.useState<UserMfaPreference | null>(null)
	const [phoneNumber, setPhoneNumber] = React.useState<string>(appState.authState.attributes?.phone_number || '')
	const [totpCode, setTotpCode] = React.useState<string>('')
	const [secretCode, setSecretCode] = React.useState<string>('') // This should be set to the user's TOTP secret code
	const [state, setState] = React.useState<'view' | 'app' | 'phone1' | 'phone2'>('view')
	const [accessToken, setAccessToken] = React.useState<string>('')

	useEffect(() => {
		if (appState.authState.isLoggedIn) {
			fetchUserData()
		}
	}, [appState.authState.isLoggedIn])

	const fetchUserData = useCallback(async () => {
		Auth.currentSession().then((session) => setAccessToken(session.getAccessToken().getJwtToken()))
		const [userPreferences] = await Promise.all([Request.get(`mfa/preferences`, appState.authState)])
		setUserPreferences(userPreferences.data.mfaPreferences)
	}, [Auth, appState.authState])

	const setPreferredMFA = async (mfaType: { sms: { enabled: boolean; preferred: boolean }; app: { enabled: boolean; preferred: boolean } }) => {
		try {
			const res = await Request.post(`mfa/preferences`, { accessToken, mfaPreferences: mfaType }, appState.authState)
			if (res.data.success) {
				props.updateMessages({
					type: 'add',
					data: { severity: 'success', message: `Preferred MFA set to ${mfaType}`, dismissible: true, timeout: 1000 },
				})
			}
		} catch (error) {
			props.updateMessages({
				type: 'add',
				data: { severity: 'danger', message: `Failed to set preferred MFA: ${error}`, dismissible: true, timeout: 1000 },
			})
		}
	}
	const setupTotpSecret = async () => {
		try {
			const res = await Request.post(`mfa/setup-totp`, { accessToken }, appState.authState)
			if (res.data.success) {
				setSecretCode(res.data.token)
				props.updateMessages({
					type: 'add',
					data: { severity: 'success', message: 'TOTP secret code generated successfully!', dismissible: true, timeout: 1000 },
				})
			}
		} catch (error) {
			props.updateMessages({
				type: 'add',
				data: { severity: 'danger', message: `Failed to generate TOTP secret code: ${error}`, dismissible: true, timeout: 1000 },
			})
		}
	}
	const verifyTotpCode = async (code: string) => {
		try {
			const res = await Request.post(`mfa/verify-totp`, { accessToken, code }, appState.authState)
			if (res.data.success) {
				setState('view')
				fetchUserData()
				props.updateMessages({
					type: 'add',
					data: { severity: 'success', message: 'TOTP code verified successfully!', dismissible: true, timeout: 1000 },
				})
			}
		} catch (error) {
			props.updateMessages({
				type: 'add',
				data: { severity: 'danger', message: `Failed to verify TOTP code: ${error}`, dismissible: true, timeout: 1000 },
			})
		}
	}
	const updatePhoneNumber = async (phoneNumber: string) => {
		try {
			const res = await Request.post(`mfa/phone`, { accessToken, phoneNumber }, appState.authState)
			if (res.data.success) {
				setState('phone2')
				props.updateMessages({
					type: 'add',
					data: { severity: 'success', message: 'Phone number updated successfully!', dismissible: true, timeout: 1000 },
				})
			}
		} catch (error) {
			props.updateMessages({
				type: 'add',
				data: { severity: 'danger', message: `Failed to update phone number: ${error}`, dismissible: true, timeout: 1000 },
			})
		}
	}
	const verifyPhoneNumber = async (code: string) => {
		try {
			const res = await Request.post(`mfa/phone`, { accessToken, code }, appState.authState)
			if (res.data.success) {
				setState('view')
				fetchUserData()
				props.updateMessages({
					type: 'add',
					data: { severity: 'success', message: 'Phone number verified successfully!', dismissible: true, timeout: 1000 },
				})
			}
		} catch (error) {
			props.updateMessages({
				type: 'add',
				data: { severity: 'danger', message: `Failed to verify phone number: ${error}`, dismissible: true, timeout: 1000 },
			})
		}
	}

	const totpURI = getTOTPURI(secretCode, appState.authState.attributes?.email || '')

	const appEnabled = userPreferences?.mfaSettingList?.includes('SOFTWARE_TOKEN_MFA') || false
	const smsEnabled = userPreferences?.mfaSettingList?.includes('SMS_MFA') || false
	const isAppPreferred = userPreferences?.preferredMfaSetting === 'SOFTWARE_TOKEN_MFA'
	const isSmsPreferred = userPreferences?.preferredMfaSetting === 'SMS_MFA'

	return (
		<Modal show={props.isOpen} onHide={props.onClose} centered>
			<Modal.Header closeButton>
				<Modal.Title>Two-Factor Authentication</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{state === 'view' && (
					<Row>
						<Col>
							<p>App Based {isAppPreferred ? <Badge variant="info">Preferred</Badge> : null}</p>
							<p>Enabled: {appEnabled ? 'Enabled' : 'Disabled'}</p>
							<p>Authenticator App: {appEnabled ? 'Active' : 'Not set'}</p>
							{!appEnabled && (
								<Button
									onClick={() => {
										setState('app')
										if (!secretCode) setupTotpSecret()
									}}
								>
									Activate MFA with App
								</Button>
							)}
							{!isAppPreferred && appEnabled && (
								<Button
									onClick={() => setPreferredMFA({ sms: { enabled: smsEnabled, preferred: false }, app: { enabled: true, preferred: true } })}
								>
									Set Preferred
								</Button>
							)}
						</Col>
						<Col>
							<p>SMS {isSmsPreferred ? <Badge variant="info">Preferred</Badge> : null}</p>
							<p>Enabled: {smsEnabled ? 'Enabled' : 'Disabled'}</p>
							<p>Phone Number: {phoneNumber || 'Not set'}</p>
							{!smsEnabled && <Button onClick={() => setState('phone1')}>Activate MFA with SMS</Button>}
							{!isSmsPreferred && smsEnabled && (
								<Button
									onClick={() => setPreferredMFA({ sms: { enabled: true, preferred: true }, app: { enabled: appEnabled, preferred: false } })}
								>
									Set Preferred
								</Button>
							)}
						</Col>
					</Row>
				)}
				{state === 'app' && (
					<Row>
						<Col>
							<ol>
								<li>Open or install your authenticator app (e.g. Microsoft Authenticator, Google Authenticator, Authy, etc.).</li>
								<li>Scan the QR code below or manually enter the secret code.</li>
								<li>Enter the TOTP code generated by your authenticator app.</li>
								<li>Click &quot;Verify TOTP Code&quot; to complete the setup.</li>
							</ol>
							{secretCode ? (
								<QRCode
									value={totpURI}
									size={256}
									style={styles.qrCode}
									viewBox={`0 0 256 256`}
									onClick={() => {
										navigator.clipboard.writeText(totpURI)
										props.updateMessages({
											type: 'add',
											data: { severity: 'success', message: 'Setup link copied to clipboard.', dismissible: true, timeout: 1000 },
										})
									}}
								/>
							) : (
								<p>Generating QR Code...</p>
							)}
							<pre>
								{secretCode} <Copy onClick={() => navigator.clipboard.writeText(secretCode)} />
							</pre>
							<p>Enter the TOTP code from your authenticator app:</p>
							<Form.Control type="text" value={totpCode} onChange={(e) => setTotpCode(e.target.value)} placeholder="TOTP Code" />
							<Button onClick={() => verifyTotpCode(totpCode)}>Verify TOTP Code</Button>
						</Col>
					</Row>
				)}
				{state === 'phone1' && (
					<>
						<Row>
							<Col>
								<ol>
									<li>Enter your phone number below.</li>
									<li>Click &quot;Send Verification Code&quot; to send verification code.</li>
								</ol>
							</Col>
						</Row>
						<Row>
							<Col>
								<PhoneInput value={phoneNumber} onChange={(value) => setPhoneNumber(value?.toString() || '')} defaultCountry="AU" />
							</Col>
							<Col>
								<Button onClick={() => updatePhoneNumber(phoneNumber)}>Send Verification Code</Button>
							</Col>
						</Row>
					</>
				)}
				{state === 'phone2' && (
					<>
						<Row>
							<Col>
								<ol>
									<li>Check your phone for the verification code.</li>
									<li>Enter the code in the input field below.</li>
									<li>Click &quot;Verify Phone Number&quot; to complete the setup.</li>
								</ol>
							</Col>
						</Row>
						<Row>
							<Col>
								<Form.Control value={totpCode} onChange={(e) => setTotpCode(e.target.value)} placeholder="Verification Code" />
							</Col>
							<Col>
								<Button onClick={() => verifyPhoneNumber(totpCode)}>Verify Phone Number</Button>
							</Col>
						</Row>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{state === 'view' ? (
					<Button variant="secondary" onClick={props.onClose}>
						Close
					</Button>
				) : (
					<button type="button" className="btn btn-secondary" onClick={() => setState('view')}>
						Back
					</button>
				)}
			</Modal.Footer>
		</Modal>
	)
}

const styles = {
	qrCode: { height: 'auto', maxWidth: '100%', width: '100%', cursor: 'pointer' },
}
